// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { useState, useEffect, useCallback } from 'react';
import { NotificationDrawer } from './NotificationDrawer.js';
import { apiClient } from '../services/api-client.js';

interface NotificationBellProps {
  t: Record<string, any>;
}

const POLL_INTERVAL_MS = 30_000;

/**
 * Bell icon with unread badge, opens the notification drawer.
 * Sprint 17.3.0: Notification System (Phase 15)
 */
export function NotificationBell({ t }: NotificationBellProps) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasCritical, setHasCritical] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const stats = (await apiClient.getNotificationStats()) as {
        unread_count?: number;
        unread_by_priority?: { critical?: number };
      };
      setUnreadCount(stats.unread_count ?? 0);
      setHasCritical((stats.unread_by_priority?.critical ?? 0) > 0);
    } catch {
      // Non-critical — silently fail
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchStats]);

  // Refresh stats when drawer closes
  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    fetchStats();
  }, [fetchStats]);

  const bellLabel = t.notifications?.bell?.title ?? 'Notifications';
  const countLabel =
    unreadCount > 0 ? `${unreadCount} ${t.notifications?.bell?.unread_count ?? 'unread'}` : '';

  return (
    <>
      <button
        className={`notif-bell ${hasCritical ? 'notif-bell-pulse' : ''}`}
        onClick={() => setDrawerOpen(!drawerOpen)}
        aria-label={`${bellLabel}${countLabel ? ` (${countLabel})` : ''}`}
        type="button"
      >
        <span className="notif-bell-icon" aria-hidden="true">
          {'\u{1F514}'}
        </span>
        {unreadCount > 0 && (
          <span className="notif-bell-badge" aria-hidden="true">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      <NotificationDrawer isOpen={drawerOpen} onClose={handleDrawerClose} t={t} />
    </>
  );
}
